import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from 'prosemirror-state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
// @ts-ignore - write-good doesn't have TypeScript definitions
import writeGood from 'write-good';
import { detectLanguage, SpellLanguage } from '../spellcheck/language';

// Proofreading Extension
const ProofreadingKey = new PluginKey('proofreading');

export interface ProofreadingOptions {
  enabled: boolean;
  settings: {
    spelling: boolean;
    grammar: boolean;
    activeVoice: boolean;
    conciseness: boolean;
    sentenceSplit: boolean;
  };
  customDictionary: string[];
  onAddToDictionary?: (word: string) => void;
}

export const Proofreading = Extension.create<ProofreadingOptions>({
  name: 'proofreading',

  addOptions() {
    return {
      enabled: false,
      settings: {
        spelling: true,
        grammar: true,
        activeVoice: false,
        conciseness: false,
        sentenceSplit: false,
      },
      customDictionary: [],
      onAddToDictionary: undefined,
    };
  },

  addProseMirrorPlugins() {
    const extension = this;
    
    return [
      new Plugin({
        key: ProofreadingKey,
        state: {
          init() {
            return DecorationSet.empty;
          },
          apply(tr, oldState) {
            if (!extension.options.enabled) {
              return DecorationSet.empty;
            }

            const decorations: Decoration[] = [];
            const doc = tr.doc;
            const { settings, customDictionary } = extension.options;
            // write-good's rules are English ones ("there is", "so", passive
            // voice), so it only reads paragraphs written in English
            const docLang = detectLanguage(doc.textContent) ?? 'en';
            const blockLang = new Map<unknown, SpellLanguage>();
            const languageOf = (pos: number) => {
              const block = doc.resolve(pos).parent;
              let lang = blockLang.get(block);
              if (!lang) {
                lang = detectLanguage(block.textContent) ?? docLang;
                blockLang.set(block, lang);
              }
              return lang;
            };

            // Process the document text
            doc.descendants((node, pos) => {
              if (!node.isText || !node.text) return;

              const text = node.text;
              
              // Check grammar using write-good
              if ((settings.grammar || settings.activeVoice || settings.conciseness) && languageOf(pos) === 'en') {
                try {
                  const suggestions = writeGood(text, {
                    passive: settings.activeVoice,
                    illusion: settings.grammar,
                    so: settings.conciseness,
                    thereIs: settings.grammar,
                    weasel: settings.conciseness,
                    adverb: settings.conciseness,
                    tooWordy: settings.conciseness,
                    cliches: settings.conciseness,
                  });

                  suggestions.forEach((suggestion: any) => {
                    const from = pos + suggestion.index;
                    const to = from + suggestion.offset;
                    
                    decorations.push(
                      Decoration.inline(from, to, {
                        class: 'grammar-error',
                        'data-suggestion': suggestion.reason,
                      })
                    );
                  });
                } catch (e) {
                  // Ignore parsing errors
                }
              }

              // Check spelling using a simple approach (words not in custom dictionary)
              if (settings.spelling) {
                // Split text into words and check each
                const words = text.split(/\b/);
                
                words.forEach((word) => {
                  if (/^[a-zA-Z]+$/.test(word)) {
                    // Check if word is in custom dictionary
                    const isInCustomDict = customDictionary.some(
                      dictWord => dictWord.toLowerCase() === word.toLowerCase()
                    );
                    
                    if (!isInCustomDict) {
                      // Let browser handle spellcheck, but we can mark unknown technical terms
                      // For now, we'll rely on the browser's native spellcheck
                    }
                  }
                });
              }
            });

            return DecorationSet.create(doc, decorations);
          },
        },
        props: {
          decorations(state) {
            return this.getState(state);
          },
          // Add context menu for "Add to Dictionary"
          handleDOMEvents: {
            contextmenu: (view, event) => {
              const target = event.target as HTMLElement;
              if (target.classList.contains('spelling-error') || 
                  target.classList.contains('grammar-error')) {
                // Could add custom context menu here
                return false;
              }
              return false;
            },
          },
        },
      }),
    ];
  },
});
